import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { FileDown, BookOpen, HelpCircle, Users, Building2, User, ChevronRight, ExternalLink } from 'lucide-react';
import { useAppConfig } from '@/hooks/useAppConfig';

const API_URL = '';

const HelpCenter = () => {
  const [loading, setLoading] = useState(false);
  const appConfig = useAppConfig();

  const handleDownloadGuide = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/guides/user-guide`);
      const data = await response.json();
      
      if (data.download_url) {
        // Trigger download
        const link = document.createElement('a');
        link.href = `${API_URL}${data.download_url}`;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('User guide downloaded successfully!');
      } else {
        throw new Error('Failed to generate guide');
      }
    } catch (error) {
      toast.error('Failed to download user guide');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const guides = [
    {
      title: 'Customer Guide',
      description: 'Learn how to search properties, use the interior design estimator, and save your favorites.',
      icon: User,
      color: 'bg-blue-100 text-blue-700',
      sections: ['Registration & Login', 'Searching Properties', 'Interior Estimator', 'Saving & Sharing']
    },
    {
      title: 'Agent Portal Guide',
      description: 'Complete guide for real estate agents to manage listings, leads, and teams.',
      icon: Users,
      color: 'bg-emerald-100 text-emerald-700',
      sections: ['Agent Registration', 'Subscription Tiers', 'Managing Listings', 'CRM & Leads']
    },
    {
      title: 'Developer Portal Guide',
      description: 'Learn how to manage projects, use the architectural builder, and publish landing pages.',
      icon: Building2,
      color: 'bg-purple-100 text-purple-700',
      sections: ['Developer Onboarding', 'Project Management', 'Builder Tools', 'Publishing']
    }
  ];

  const faqs = [
    { q: 'How do I reset my password?', a: 'Click "Forgot Password" on the login page and follow the reset link.' },
    { q: 'Is my data secure?', a: 'Yes, we use industry-standard encryption and are DPDP Act 2023 compliant.' },
    { q: 'How accurate is the Interior Estimator?', a: 'Our Z-Engine provides estimates within 10-15% accuracy.' },
    { q: 'Can I switch subscription plans?', a: 'Yes, you can upgrade anytime. Downgrades take effect at billing cycle end.' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="help-center-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
              <span className="text-white font-bold">Z</span>
            </div>
            <div>
              <h1 className="font-bold text-slate-900">ZenStudios Help Center</h1>
              <p className="text-xs text-slate-500">Resources & Documentation</p>
            </div>
          </div>
          <Button onClick={() => window.location.href = '/'} variant="outline" className="rounded-xl">
            Back to Home
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <HelpCircle className="w-16 h-16 mx-auto mb-6 opacity-80" />
          <h1 className="text-4xl font-bold mb-4">How can we help you?</h1>
          <p className="text-slate-300 max-w-2xl mx-auto mb-8">
            Find guides, documentation, and answers to frequently asked questions about ZenStudios platform.
          </p>
          
          <Button 
            onClick={handleDownloadGuide}
            disabled={loading}
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl px-8 py-6 text-lg"
          >
            <FileDown className="w-5 h-5 mr-2" />
            {loading ? 'Generating PDF...' : 'Download Complete User Guide (PDF)'}
          </Button>
        </div>
      </section>

      {/* Guide Sections */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 mb-8 text-center">User Guides by Role</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {guides.map((guide, index) => (
              <div key={index} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-shadow">
                <div className={`w-14 h-14 rounded-xl ${guide.color} flex items-center justify-center mb-4`}>
                  <guide.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2">{guide.title}</h3>
                <p className="text-slate-600 text-sm mb-4">{guide.description}</p>
                
                <div className="space-y-2">
                  {guide.sections.map((section, i) => (
                    <div key={i} className="flex items-center text-sm text-slate-500">
                      <ChevronRight className="w-4 h-4 mr-1 text-slate-400" />
                      {section}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 mb-8 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-2">{faq.q}</h3>
                <p className="text-slate-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button onClick={handleDownloadGuide} variant="outline" className="rounded-xl">
              <BookOpen className="w-4 h-4 mr-2" />
              View All FAQs in PDF Guide
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Still need help?</h2>
          <p className="text-slate-600 mb-8">Our support team is available Monday to Saturday, 9 AM to 6 PM IST</p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`mailto:${appConfig.support_email}`} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-6 py-3 text-slate-700 hover:shadow-md transition-shadow" data-testid="help-support-email">
              <ExternalLink className="w-4 h-4" />
              {appConfig.support_email}
            </a>
            <a href={`tel:${appConfig.support_phone_tel}`} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-6 py-3 text-slate-700 hover:shadow-md transition-shadow" data-testid="help-support-phone">
              {appConfig.support_phone}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-400">© 2025 ZenStudios. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenter;
